import { db } from "../config/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// Coleção no Firestore para configurações do Google Maps
const MAPS_COLLECTION = "mapsConfig";

// ============================================
// FUNÇÕES DIRETAS NO FIRESTORE (sem backend)
// ============================================

export async function apiGetMapsConfig(grupoId) {
  if (!grupoId || typeof grupoId !== "string") {
    throw new Error("grupoId inválido");
  }

  try {
    const docRef = doc(db, MAPS_COLLECTION, grupoId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return {
        hasKey: false,
        updatedAt: null,
      };
    }

    const data = docSnap.data();
    return {
      hasKey: !!data?.mapsKey,
      updatedAt: data?.updatedAt || null,
    };
  } catch (error) {
    console.error("[apiGetMapsConfig] Erro:", error);
    throw new Error("Erro ao buscar configuração do Maps");
  }
}

export async function apiSaveMapsKey({ grupoId, apiKey }) {
  if (!grupoId || typeof grupoId !== "string") {
    throw new Error("grupoId inválido");
  }

  // 🔍 LOG DE DEBUG - REMOVER DEPOIS
  console.log("📝 [apiSaveMapsKey] Iniciando salvamento...");
  console.log("   grupoId:", grupoId);
  console.log("   apiKey length:", apiKey?.length);

  try {
    const docRef = doc(db, MAPS_COLLECTION, grupoId);
    console.log("   Documento:", `${MAPS_COLLECTION}/${grupoId}`);
    
    const updateData = {
      updatedAt: serverTimestamp(),
    };

    if (typeof apiKey === "string" && apiKey.trim()) {
      updateData.mapsKey = apiKey.trim();
    } else {
      updateData.mapsKey = null;
    }

    console.log("   Dados a salvar:", Object.keys(updateData));
    console.log("   Chamando setDoc...");
    
    await setDoc(docRef, updateData, { merge: true });

    console.log("   ✅ setDoc executado com sucesso!");
    
    return {
      hasKey: !!updateData.mapsKey,
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error("[apiSaveMapsKey] Erro completo:", error);
    console.error("   Código:", error.code);
    console.error("   Mensagem:", error.message);
    console.error("   Stack:", error.stack);
    
    // Mensagens de erro mais específicas
    if (error.code === "permission-denied") {
      throw new Error(
        "Permissão negada. Verifique se: " +
        "1) Seu usuário é Admin/Gestor (nível 5 ou 6), " +
        "2) As regras do Firestore foram deployadas, " +
        "3) Você está logado corretamente"
      );
    }
    
    if (error.code === "unavailable") {
      throw new Error("Firestore indisponível. Verifique sua conexão com a internet");
    }
    
    throw new Error(`Erro ao salvar API Key: ${error.message || error.code || "Erro desconhecido"}`);
  }
}

export async function apiDeleteMapsKey(grupoId) {
  if (!grupoId || typeof grupoId !== "string") {
    throw new Error("grupoId inválido");
  }

  try {
    const docRef = doc(db, MAPS_COLLECTION, grupoId);
    
    await setDoc(docRef, {
      mapsKey: null,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    return { ok: true };
  } catch (error) {
    console.error("[apiDeleteMapsKey] Erro:", error);
    throw new Error("Erro ao deletar API Key do Maps");
  }
}

// Função auxiliar para obter a chave em texto plano
export async function getMapsKeyPlain(grupoId) {
  if (!grupoId || typeof grupoId !== "string") {
    throw new Error("grupoId inválido");
  }

  try {
    const docRef = doc(db, MAPS_COLLECTION, grupoId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return "";
    }

    const data = docSnap.data();
    return data?.mapsKey || "";
  } catch (error) {
    console.error("[getMapsKeyPlain] Erro:", error);
    return "";
  }
}

// ============================================
// GOOGLE MAPS JAVASCRIPT API (Browser)
// Usa a chave configurada no app, sem backend
// ============================================

// Cache global da API do Google Maps
let googleMapsLoader = null;
let googleMapsApiKey = null;

const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 12000;
const GOOGLE_MAPS_REQUEST_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, message) {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([
    promise.finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    }),
    timeoutPromise,
  ]);
}

/**
 * Carrega a Google Maps JavaScript API
 * @param {string} apiKey - Chave da API do Google Maps
 * @returns {Promise<typeof google>}
 */
async function loadGoogleMapsAPI(apiKey) {
  // Se já foi carregada com a mesma chave, retorna
  if (googleMapsLoader && googleMapsApiKey === apiKey) {
    return googleMapsLoader;
  }

  // Se já existe mas com chave diferente, remove o script antigo
  if (googleMapsApiKey && googleMapsApiKey !== apiKey) {
    const oldScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (oldScript) {
      oldScript.remove();
    }
    googleMapsLoader = null;
    googleMapsApiKey = null;
  }

  // Carrega a API
  googleMapsApiKey = apiKey;
  googleMapsLoader = new Promise((resolve, reject) => {
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
    };

    timeoutId = setTimeout(() => {
      cleanup();
      delete window.initGoogleMaps;
      reject(new Error("Tempo limite ao carregar Google Maps API"));
    }, GOOGLE_MAPS_LOAD_TIMEOUT_MS);

    // Se já existe window.google, usa direto
    if (window.google?.maps) {
      cleanup();
      resolve(window.google);
      return;
    }

    // Callback global que será chamado quando o script carregar
    window.initGoogleMaps = () => {
      cleanup();
      delete window.initGoogleMaps;
      resolve(window.google);
    };

    // Adiciona o script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps&language=pt-BR`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      cleanup();
      delete window.initGoogleMaps;
      reject(new Error("Erro ao carregar Google Maps API"));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoader;
}

/**
 * Autocomplete de endereços (Brasil)
 */
export async function apiMapsAutocomplete({ grupoId, input }) {
  if (!grupoId || !input) {
    throw new Error("grupoId e input são obrigatórios");
  }

  const apiKey = await getMapsKeyPlain(grupoId);
  if (!apiKey) {
    throw new Error("❌ API Key do Google Maps não configurada. Configure em Opções > Google Maps");
  }

  try {
    await withTimeout(
      loadGoogleMapsAPI(apiKey),
      GOOGLE_MAPS_LOAD_TIMEOUT_MS,
      "Tempo limite ao carregar Google Maps API"
    );
    
    return new Promise((resolve, reject) => {
      const service = new google.maps.places.AutocompleteService();

      const timeoutId = setTimeout(() => {
        reject(new Error("Tempo limite no autocomplete"));
      }, GOOGLE_MAPS_REQUEST_TIMEOUT_MS);
      
      service.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: "br" },
          language: "pt-BR",
        },
        (predictions, status) => {
          clearTimeout(timeoutId);
          if (status !== google.maps.places.PlacesServiceStatus.OK && 
              status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            reject(new Error(`Erro no autocomplete: ${status}`));
            return;
          }

          const results = (predictions || []).map((p) => ({
            placeId: p.place_id,
            description: p.description,
          }));

          resolve(results);
        }
      );
    });
  } catch (error) {
    console.error("[apiMapsAutocomplete] Erro:", error);
    throw error;
  }
}

/**
 * Detalhes de um lugar (endereço, coordenadas)
 */
export async function apiMapsPlaceDetails({ grupoId, placeId }) {
  if (!grupoId || !placeId) {
    throw new Error("grupoId e placeId são obrigatórios");
  }

  const apiKey = await getMapsKeyPlain(grupoId);
  if (!apiKey) {
    throw new Error("❌ API Key do Google Maps não configurada. Configure em Opções > Google Maps");
  }

  try {
    await withTimeout(
      loadGoogleMapsAPI(apiKey),
      GOOGLE_MAPS_LOAD_TIMEOUT_MS,
      "Tempo limite ao carregar Google Maps API"
    );
    
    return new Promise((resolve, reject) => {
      // Precisa de um elemento div para o PlacesService
      const div = document.createElement("div");
      const service = new google.maps.places.PlacesService(div);

      const timeoutId = setTimeout(() => {
        div.remove();
        reject(new Error("Tempo limite ao buscar detalhes"));
      }, GOOGLE_MAPS_REQUEST_TIMEOUT_MS);
      
      service.getDetails(
        {
          placeId,
          fields: ["formatted_address", "geometry", "name"],
          language: "pt-BR",
        },
        (place, status) => {
          clearTimeout(timeoutId);
          div.remove();
          
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            reject(new Error(`Erro ao buscar detalhes: ${status}`));
            return;
          }

          resolve({
            address: place.formatted_address || "",
            lat: place.geometry?.location?.lat() || null,
            lng: place.geometry?.location?.lng() || null,
            name: place.name || "",
          });
        }
      );
    });
  } catch (error) {
    console.error("[apiMapsPlaceDetails] Erro:", error);
    throw error;
  }
}

/**
 * Calcula distância entre dois pontos
 */
export async function apiMapsDistance({ grupoId, origin, destination }) {
  if (!grupoId || !origin || !destination) {
    throw new Error("grupoId, origin e destination são obrigatórios");
  }

  const apiKey = await getMapsKeyPlain(grupoId);
  if (!apiKey) {
    throw new Error("❌ API Key do Google Maps não configurada. Configure em Opções > Google Maps");
  }

  try {
    await withTimeout(
      loadGoogleMapsAPI(apiKey),
      GOOGLE_MAPS_LOAD_TIMEOUT_MS,
      "Tempo limite ao carregar Google Maps API"
    );
    
    return new Promise((resolve, reject) => {
      const service = new google.maps.DistanceMatrixService();

      const timeoutId = setTimeout(() => {
        reject(new Error("Tempo limite ao calcular distancia"));
      }, GOOGLE_MAPS_REQUEST_TIMEOUT_MS);
      
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
          language: "pt-BR",
        },
        (response, status) => {
          clearTimeout(timeoutId);
          if (status !== google.maps.DistanceMatrixStatus.OK) {
            reject(new Error(`Erro ao calcular distância: ${status}`));
            return;
          }

          const element = response.rows[0]?.elements[0];
          if (!element || element.status !== "OK") {
            reject(new Error(element?.status || "Nenhum resultado"));
            return;
          }

          resolve({
            distanceMeters: element.distance?.value || 0,
            distanceText: element.distance?.text || "",
            duration: element.duration?.value || 0,
            durationText: element.duration?.text || "",
          });
        }
      );
    });
  } catch (error) {
    console.error("[apiMapsDistance] Erro:", error);
    throw error;
  }
}

function toWaypoint(value) {
  if (!value) return null;
  if (typeof value === "string") {
    return { address: value };
  }
  if (typeof value === "object" && value.lat != null && value.lng != null) {
    return { location: { latLng: { latitude: value.lat, longitude: value.lng } } };
  }
  return null;
}

function sumEstimatedPrice(prices = []) {
  let total = 0;
  let currencyCode = null;
  prices.forEach((p) => {
    const units = Number(p?.units || 0);
    const nanos = Number(p?.nanos || 0);
    const value = units + nanos / 1e9;
    if (!Number.isNaN(value)) total += value;
    if (!currencyCode && p?.currencyCode) currencyCode = p.currencyCode;
  });
  return { total, currencyCode };
}

/**
 * Calcula o valor estimado de pedagio usando Google Routes API
 */
export async function apiMapsTolls({ grupoId, origin, destination }) {
  if (!grupoId || !origin || !destination) {
    throw new Error("grupoId, origin e destination são obrigatórios");
  }

  const apiKey = await getMapsKeyPlain(grupoId);
  if (!apiKey) {
    throw new Error("❌ API Key do Google Maps não configurada. Configure em Opções > Google Maps");
  }

  const originWp = toWaypoint(origin);
  const destWp = toWaypoint(destination);
  if (!originWp || !destWp) {
    throw new Error("Origem/destino inválidos para cálculo de pedágio");
  }

  try {
    const resp = await withTimeout(
      fetch("https://routes.googleapis.com/directions/v2:computeRoutes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": "routes.travelAdvisory.tollInfo",
        },
        body: JSON.stringify({
          origin: originWp,
          destination: destWp,
          travelMode: "DRIVE",
          routingPreference: "TRAFFIC_AWARE",
          languageCode: "pt-BR",
          regionCode: "BR",
          computeAlternativeRoutes: false,
          extraComputations: ["TOLLS"],
          routeModifiers: {
            avoidTolls: false,
          },
        }),
      }),
      GOOGLE_MAPS_REQUEST_TIMEOUT_MS,
      "Tempo limite ao calcular pedágio"
    );

    const json = await resp.json();
    if (!resp.ok) {
      const msg = json?.error?.message || json?.error?.status || resp.statusText;
      throw new Error(String(msg || "Erro ao calcular pedágio"));
    }

    const tollInfo = json?.routes?.[0]?.travelAdvisory?.tollInfo || null;
    const { total, currencyCode } = sumEstimatedPrice(tollInfo?.estimatedPrice || []);

    return {
      tollValue: Number.isFinite(total) ? total : 0,
      currencyCode: currencyCode || "",
    };
  } catch (error) {
    console.error("[apiMapsTolls] Erro:", error);
    throw error;
  }
}
